/**
 * Collector connector records for Settings › Collectors.
 * All timestamps derive from FROZEN_NOW.
 */

import { FROZEN_NOW_MS } from '@/lib/frozenTime';

export type CollectorStatus = 'connected' | 'disconnected' | 'error';
export type LastSyncStatus = 'success' | 'partial' | 'failed' | null;

export interface Collector {
  id: string;
  name: string;
  type: 'insightful' | 'manual';
  status: CollectorStatus;
  /**
   * Timestamp of the most recent sync attempt (from `lastSync.finishedAt`
   * when finished, else `startedAt`). Empty string when the collector has
   * never run a sync. Renamed from `lastHeartbeat` in task 105a — heartbeat
   * belongs to the worker-level signal, not per-collector sync state.
   */
  lastSyncAt: string;
  /**
   * Seconds until the next sync is expected. Derived client-side from
   * `lastSync.finishedAt + pollInterval`. Zero / negative → due now.
   */
  nextSyncInS: number;
  connectedAt: string;
  recordsFetched: number | null;
  recordsUpserted: number | null;
  lastSyncStatus: LastSyncStatus;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const COLLECTORS = [
  {
    id: 'col-insightful-prod',
    name: 'Insightful — Production Workspace',
    type: 'insightful',
    status: 'connected',
    lastSyncAt: new Date(FROZEN_NOW_MS - 45 * 1000).toISOString(),
    nextSyncInS: 315,
    connectedAt: new Date(FROZEN_NOW_MS - 47 * DAY_MS).toISOString(),
    recordsFetched: 245,
    recordsUpserted: 238,
    lastSyncStatus: 'success',
  },
  {
    id: 'col-insightful-archive',
    name: 'Insightful — Archive Workspace',
    type: 'insightful',
    status: 'connected',
    lastSyncAt: new Date(FROZEN_NOW_MS - 3 * MINUTE_MS).toISOString(),
    nextSyncInS: 240,
    connectedAt: new Date(FROZEN_NOW_MS - 30 * DAY_MS).toISOString(),
    recordsFetched: 82,
    recordsUpserted: 79,
    lastSyncStatus: 'success',
  },
  {
    id: 'col-manual-timesheet',
    name: 'Manual Timesheet Upload',
    type: 'manual',
    status: 'disconnected',
    lastSyncAt: new Date(FROZEN_NOW_MS - 18 * HOUR_MS).toISOString(),
    nextSyncInS: 0,
    connectedAt: new Date(FROZEN_NOW_MS - 75 * DAY_MS).toISOString(),
    recordsFetched: null,
    recordsUpserted: null,
    lastSyncStatus: null,
  },
  {
    id: 'col-insightful-legacy',
    name: 'Insightful — Legacy Workspace',
    type: 'insightful',
    status: 'error',
    lastSyncAt: new Date(FROZEN_NOW_MS - 6 * HOUR_MS).toISOString(),
    nextSyncInS: 600,
    connectedAt: new Date(FROZEN_NOW_MS - 120 * DAY_MS).toISOString(),
    recordsFetched: 0,
    recordsUpserted: 0,
    lastSyncStatus: 'failed',
  },
] as const satisfies readonly Collector[];
